// supervisor-candidate-review-phase.ts
//
// Whole "DAG exhausted" branch of `runSupervisorUntilIdle`'s tick loop —
// split out of supervisor-run-until-idle.ts into its OWN file (plan
// 20260924-1400 follow-up, O-1 fix) rather than folded into
// supervisor-candidate-review-dispatch.ts, so
// `dispatchCandidateReviewSessionIfNeeded` keeps a caller OUTSIDE its own
// module — authority-critical-functions-have-production-callers.test.ts
// asserts exactly that (a same-module caller would not catch the wiring
// getting severed again, the P20 shape that check exists for).
//
// O-1 fix (dogfood recheck 260925 follow-up): checks "both reviews already
// settled" BEFORE the §18 gate — otherwise the tick right after the LAST
// review session finished (quiet_hours just opened, or the session cap just
// filled) reported `before_dispatch_blocked`/`blocked:<code>` for a mission
// that was actually DONE (no new dispatch was ever going to happen). The gate
// only makes sense for a dispatch about to occur.
import { describeBlockedTicketVerdicts } from "./blocked-ticket-verdict-observability.js";
import {
  CLEAN_REVIEW_CANDIDATE_TICKET_ID,
  REVIEW_CANDIDATE_TICKET_ID,
} from "./controller-reserved-ticket-ids.js";
import {
  checkBeforeDispatchGate,
  mapBeforeDispatchGateVerdictToResult,
} from "./supervisor-before-dispatch-gate-step.js";
import { dispatchCandidateReviewSessionIfNeeded } from "./supervisor-candidate-review-dispatch.js";
import { readCandidateReviewSettlement } from "./supervisor-candidate-review-settlement.js";
import type { RunUntilIdleInput, RunUntilIdleResult } from "./supervisor-run-until-idle-types.js";

/**
 * Returns a `RunUntilIdleResult` when the tick loop must return, or
 * `undefined` when the caller should `continue` to the next tick (a review
 * session was just dispatched).
 */
export async function runCandidateReviewPhase(
  input: RunUntilIdleInput,
  ticksRun: number,
  g2UnenforcedConditions: readonly string[] | undefined,
): Promise<RunUntilIdleResult | undefined> {
  // H1 (code review P3): settled PER CANDIDATE — a review pair that ran for
  // an older candidate is a dispatch about to happen, so it clears the gate too.
  const settlement = await readCandidateReviewSettlement(input);
  if (!(settlement.local && settlement.clean)) {
    // NEW-1 fix (dogfood recheck 260925): a review session is a REAL
    // dispatch, so it must clear the SAME §18 gate a feature ticket does
    // BEFORE it runs — measured on real mission B: 4 review sessions
    // dispatched inside an active quiet_hours window (~$0.73 burned, no
    // `blocked:quiet_hours` anywhere) because this branch used to skip
    // `checkBeforeDispatchGate` entirely. Same verdict mapping as the
    // feature-ticket gate (mapBeforeDispatchGateVerdictToResult).
    const reviewGate = await checkBeforeDispatchGate(input);
    const reviewGateResult = mapBeforeDispatchGateVerdictToResult(
      reviewGate,
      ticksRun,
      g2UnenforcedConditions,
    );
    if (reviewGateResult) return reviewGateResult;
  }

  // P20: the DAG is exhausted, so the candidate exists — the only point at
  // which `review-local-v1` can review it. See supervisor-candidate-review-
  // dispatch.ts's header for why it is dispatched here rather than seeded as
  // a ticket, and why its outcome does not gate "done".
  const reviewDispatch = await dispatchCandidateReviewSessionIfNeeded(input, settlement);
  if (reviewDispatch === "dispatched") return undefined;
  if (reviewDispatch === "reoffer_refused") {
    // Review of the last follow-ups M1: never a silent `done` — the stale
    // review still reads `done`, so nothing below would flag it.
    return {
      outcome: "blocked",
      ticksRun,
      g2UnenforcedConditions,
      haltReason:
        "phiên review candidate (T-9001/T-9002) đã được chạy lại cho HEAD hiện tại trong lần `run` này mà " +
        "journal vẫn chưa ghi candidate đã review — dừng để không lặp phiên trả tiền; xem journal rồi `run` lại.",
    };
  }
  if (reviewDispatch === "gate_blocked") {
    // The review session needs G0/G1/G2 like any non-bootstrap ticket.
    // Terminal for this `run`, exactly like the feature-ticket gate_blocked
    // branch: only a human approving the gate can change it, so looping
    // would spin to maxTicks and report `idle` — which is what real mission
    // ME-2026-dc845a did before this branch existed, after burning two
    // worker sessions with no actionable reason surfaced.
    return { outcome: "blocked", ticksRun, g2UnenforcedConditions };
  }
  // Pre-existing bug (found during phase-04's M1 fix, dogfood 260925): when
  // `reviewDispatch` is "not_ready" because a review ticket (T-9001/T-9002)
  // is durably "blocked" (ran out of retries — see retry-policy.ts), it is a
  // DEAD END: `reviewCandidateSessionAlreadyRan`'s SETTLED_REVIEW_STATUSES
  // does not include "blocked", so this branch keeps being re-entered every
  // tick, and `scheduler.isReady` never re-offers a "blocked" ticket either.
  // Before this fix the code fell through straight to `{outcome: "done"}` —
  // a false "done" that hid a stuck review from the operator and, for
  // T-9001 specifically, permanently starved T-9002 (it only ever dispatches
  // once T-9001 reaches "done"). Only a "blocked" review ticket is a genuine
  // dead end worth reporting here; any other "not_ready" cause (e.g. a
  // ticket left "in_progress" by an orphaned attempt) is transient and keeps
  // the existing "done" fallthrough — orphan recovery already surfaces that
  // case on the next `run` (see BUG-2 in blocked-ticket-verdict-observability.ts).
  const reviewTicketStatus: Record<string, string> = {};
  for (const id of [REVIEW_CANDIDATE_TICKET_ID, CLEAN_REVIEW_CANDIDATE_TICKET_ID]) {
    const status = input.store.state.tickets[id];
    if (status !== undefined) reviewTicketStatus[id] = status;
  }
  const blockedReviewLines = describeBlockedTicketVerdicts(
    reviewTicketStatus,
    input.store.state.ticket_last_verdict,
  );
  if (blockedReviewLines.length > 0) {
    return {
      outcome: "blocked",
      ticksRun,
      g2UnenforcedConditions,
      haltReason: `${blockedReviewLines.join(" · ")} — sửa rồi \`avatar autopilot run\` lại.`,
    };
  }
  return { outcome: "done", ticksRun, g2UnenforcedConditions };
}
